import {
  useAccessTokenReceived,
  useCustomMutation,
  useGrippContext,
  useInvalidateConfig,
  useSendAuthCode,
  useStorageKeys,
} from '@gripp/shared-logic';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { OtpInput } from 'react-native-otp-entry';
import { Button } from '../../components/crop-circle/Button';
import { colors, typography } from '../../components/crop-circle/tokens';
import { getFormattedPhoneNumber } from '../../components/forms/controlledPhoneInput';
import { Colors } from '../../themes/colors';
import { LoginProps } from './props';

export const VerifyAuthCode = (props: LoginProps) => {
  const { t } = useTranslation();

  const { mutateAsync } = useCustomMutation({
    query: validateAuthCode,
  });

  const { sendAuthCodeAsync } = useSendAuthCode();

  const [sentTo, setSentTo] = useState<string>('');
  const [pendingCode, setPendingCode] = useState<string | undefined>(undefined);
  const [hasError, setHasError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const otpRef = useRef<any>(null);

  const { storageHandler } = useGrippContext();
  const storageKeys = useStorageKeys();
  const onAccessTokenReceived = useAccessTokenReceived();
  const invalidateConfig = useInvalidateConfig();

  const getAuthCodeInput = async () => {
    return JSON.parse(
      (await storageHandler.getItem(storageKeys.authCodeInput)) as string
    );
  };

  useEffect(() => {
    const getSentTo = async () => {
      const authCodeInput = await getAuthCodeInput();
      setSentTo(
        authCodeInput.phone
          ? getFormattedPhoneNumber(
              authCodeInput.phone.countryCode,
              authCodeInput.phone.callingCode,
              authCodeInput.phone.phoneNumber
            )
          : authCodeInput.email
      );
    };

    getSentTo();
  }, []);

  const onResendCode = async () => {
    await sendAuthCodeAsync({ ...(await getAuthCodeInput()) }, () => {});
    setHasError(false);
    setPendingCode(undefined);
    otpRef.current?.clear?.();
  };

  const onFilled = (code: string) => {
    setPendingCode(code);
    setHasError(false);
  };

  const onContinue = async () => {
    if (!pendingCode) return;
    setIsSubmitting(true);
    const input = {
      ...JSON.parse(
        (await storageHandler.getItem(storageKeys.authCodeInput)) as string
      ),
      code: pendingCode,
    };
    await mutateAsync(
      { input },
      {
        onSettled: async (data: any, error: any) => {
          const result = data?.authentication?.validateAuthCode;

          if (result?.success) {
            const redirectPath = await storageHandler.getItem(
              storageKeys.redirectPath
            );
            const { accessToken } = result;
            await onAccessTokenReceived(accessToken, false, true);
            await invalidateConfig();

            if (props.handleRedirect) {
              if (props.isNewAccount) {
                await storageHandler.setItem(storageKeys.newRegister, '');
                await props.handleRedirect('/onboarding');
              } else {
                await props.handleRedirect(redirectPath || '/');
              }
            }
          } else {
            setHasError(true);
            setPendingCode(undefined);
          }

          setIsSubmitting(false);
        },
      }
    );
  };

  const canSubmit = !!pendingCode && !hasError && !isSubmitting;

  return (
    <View style={styles.container}>
      <View style={styles.headerSection}>
        <Text style={styles.verifyTitle}>{t('login.verify')}</Text>
        <Text style={styles.verifySubtitle}>
          {t('login.verifyCodeSentTo')} {sentTo}
        </Text>
      </View>

      <OtpInput
        ref={otpRef}
        autoFocus
        numberOfDigits={4}
        focusColor={hasError ? Colors.redText : colors.neutral[800]}
        onFilled={onFilled}
        onTextChange={() => {
          if (hasError) {
            setHasError(false);
            setPendingCode(undefined);
          }
        }}
        theme={{
          containerStyle: { ...styles.otpContainer, gap: 16 },
          pinCodeContainerStyle: hasError
            ? styles.pinCodeContainerError
            : styles.pinCodeContainer,
          pinCodeTextStyle: hasError ? styles.pinCodeTextError : undefined,
        }}
      />

      <View style={styles.bottomSection}>
        <Button
          variant="rectangular"
          size="large"
          state={canSubmit ? 'active' : 'default'}
          disabled={!canSubmit}
          loading={isSubmitting}
          onPress={onContinue}
          style={{ width: '100%' }}
        >
          {t('login.continue')}
        </Button>

        <View style={styles.resendTextContainer}>
          <Text style={styles.resendText}>{t('login.didntReceiveCode')}</Text>
          <Text style={styles.resendText}>
            <Text style={styles.resendLink} onPress={onResendCode}>
              {t('login.resendCode')}
            </Text>
            {'  '}
            {t('or')}
            {'  '}
            <Text
              style={styles.resendLink}
              onPress={() => props.onTryAnotherMethod?.()}
            >
              {t('login.tryAnotherMethod')}
            </Text>
          </Text>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignSelf: 'stretch',
    gap: 40,
  },
  headerSection: {
    gap: 8,
    alignItems: 'flex-start' as const,
  },
  verifyTitle: {
    ...(Platform.OS === 'web'
      ? typography.display.largeEmphasised
      : typography.display.smallEmphasised),
    textAlign: 'left',
    color: colors.neutral[800],
  },
  verifySubtitle: {
    ...typography.body.small,
    textAlign: 'left',
    color: colors.neutral[800],
  },
  otpContainer: {
    alignSelf: 'stretch',
  },
  pinCodeContainer: {
    backgroundColor: Colors.white,
    height: 52,
    flex: 1,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.graySpacer,
  },
  pinCodeContainerError: {
    backgroundColor: Colors.white,
    height: 52,
    flex: 1,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.redText,
  },
  pinCodeTextError: {
    color: Colors.redText,
  },
  bottomSection: {
    gap: 16,
  },
  resendTextContainer: {
    gap: 8,
    alignItems: 'flex-start' as const,
  },
  resendText: {
    ...typography.body.small,
    color: colors.slate[400],
    textAlign: 'left',
  },
  resendLink: {
    ...typography.body.small,
    color: colors.slate[400],
    textDecorationLine: 'underline',
  },
});

const validateAuthCode = `
  mutation Mutation($input: ValidateAuthCodeInput!) {
    authentication {
      validateAuthCode(input: $input) {
        success
        message
        accessToken
      }
    }
  }
`;
